/**
 * util — the small value predicates the merge engine needs.
 *
 * Everything the engine reads is JSON: plain objects, arrays, strings, numbers,
 * booleans and null. Nothing here handles Dates, Maps or class instances,
 * because a model file containing one fails 30.03.01's round-trip assertion
 * before it ever reaches a patch.
 */

export const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

export const isPlainObject = (v) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)

export const isScalar = (v) =>
    v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'

/** A patch object of the form `{ $ops: [...] }`. */
export const isOpsDoc = (v) => isPlainObject(v) && hasOwn(v, '$ops')

/**
 * A patch object of the form `{ $value: <literal> }` — "write exactly this".
 * RFC 7386 cannot set a key to `null` (null deletes) and cannot put an object
 * where a scalar was (an object patch recurses). Both are legitimate edits:
 * the model holds over a hundred literal nulls, many of them `setValues`
 * entries that clear a field, where absent and null mean different things.
 */
export const isValueDoc = (v) => isPlainObject(v) && hasOwn(v, '$value')

/** The keys a model file may never contain, because a patch gives them meaning. */
export const RESERVED_KEYS = ['$ops', '$value']

/** Structural equality over JSON values. Key order does not matter. */
export function deepEqual(a, b) {
    if (a === b) return true
    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) return false
        for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false
        return true
    }
    if (isPlainObject(a)) {
        if (!isPlainObject(b)) return false
        const ka = Object.keys(a)
        if (ka.length !== Object.keys(b).length) return false
        for (const k of ka) if (!hasOwn(b, k) || !deepEqual(a[k], b[k])) return false
        return true
    }
    return false
}

/**
 * True when a literal value contains a reserved key (`$ops`, `$value`) anywhere.
 * They are reserved (30.03.02 §4.1): a literal carrying one would turn into an
 * instruction the next time it passed through a patch.
 */
export function containsReserved(v) {
    if (Array.isArray(v)) return v.some(containsReserved)
    if (isPlainObject(v)) {
        for (const k of Object.keys(v)) {
            if (RESERVED_KEYS.includes(k) || containsReserved(v[k])) return true
        }
    }
    return false
}
